"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { BankIcon, StorefrontIcon } from "@phosphor-icons/react";
import StaticMapImage from "@/components/map/StaticMapImage";
import { CategoryIconMapPin } from "@/components/map/CategoryIconMapPin";
import type { EmbeddedProduct } from "@/lib/api/blog.server";
import { cn } from "@/lib/utils";
import {
  concertBranchPopupPosition,
  fitConcertNearbyMap,
} from "@/lib/map/concert-nearby-geometry";
import { ProductCardEmbed } from "./ProductCardEmbed";

interface VenueMarker {
  name: string;
  latitude: number;
  longitude: number;
}

interface BranchMarker {
  id: string;
  partnerName: string | null;
  name: string;
  displayName: string;
  address: string | null;
  latitude: number;
  longitude: number;
  distanceKm: number;
  productCount: number;
  products: EmbeddedProduct[];
}

interface Labels {
  mapTitle: string;
  mapAlt: string;
  venueMarker: string;
  branchMarker: string;
  branchesTitle: string;
  branchesEmpty: string;
  productCountTemplate: string;
  branchStatus: string;
  selectedProductsTitle: string;
  selectedProductsDescription: string;
  selectBranchTitle: string;
  selectBranchDescription: string;
  selectedProductsEmpty: string;
  selectHint: string;
  allProductsTitle: string;
  allProductsEmpty: string;
}

interface Props {
  venue: VenueMarker;
  branches: BranchMarker[];
  products: EmbeddedProduct[];
  labels: Labels;
}

export function ConcertNearbyMapClient({
  venue,
  branches,
  products,
  labels,
}: Props) {
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const selectedBranch = useMemo(
    () => branches.find((branch) => branch.id === selectedBranchId) ?? null,
    [branches, selectedBranchId],
  );
  const popupBranch = selectedBranch;
  const activeProducts = useMemo(() => {
    if (!selectedBranch) return products;
    return selectedBranch.products;
  }, [products, selectedBranch]);
  const productTitle = selectedBranch
    ? labels.selectedProductsTitle
    : labels.allProductsTitle;

  // Measured content-box size of the map container. Pins and the Static Maps
  // request share this box, so they use the same Web Mercator projection.
  const mapRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ cw: number; ch: number } | null>(null);
  const [popupHeight, setPopupHeight] = useState(96);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw <= 0 || ch <= 0) return;
      setSize((prev) =>
        prev && prev.cw === cw && prev.ch === ch ? prev : { cw, ch },
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = popupRef.current;
    if (!el) return;
    const measure = () => setPopupHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedBranchId]);

  // Overview fits every location. Selecting a branch reframes the map around
  // that branch and the venue so nearby pins are no longer compressed by
  // distant airport branches.
  const geometry = useMemo(() => {
    if (!size) return null;
    return fitConcertNearbyMap(
      { width: size.cw, height: size.ch },
      venue,
      branches,
      selectedBranch,
    );
  }, [venue, branches, selectedBranch, size]);

  // Multiple approved branches may share one building coordinate. Render one
  // accurate pin there and cycle through its branches when that pin is tapped.
  const branchGroups = useMemo(() => {
    const groups = new Map<string, BranchMarker[]>();
    for (const branch of branches) {
      const key = `${branch.latitude.toFixed(6)},${branch.longitude.toFixed(
        6,
      )}`;
      const group = groups.get(key) ?? [];
      group.push(branch);
      groups.set(key, group);
    }
    return [...groups.entries()].map(([key, items]) => ({ key, items }));
  }, [branches]);

  const summaryLine = selectedBranch
    ? [selectedBranch.name, selectedBranch.address].filter(Boolean).join(" · ")
    : "";

  return (
    <div className="mt-5 space-y-4">
      <div
        ref={mapRef}
        className="relative h-96 overflow-hidden rounded-2xl border border-zinc-200 bg-white"
      >
        {geometry && (
          <>
            <StaticMapImage
              center={geometry.center}
              zoom={geometry.zoom}
              showMarkers={false}
              loading="eager"
              title={labels.mapTitle}
              alt={labels.mapAlt}
            />
            <button
              type="button"
              className="absolute inset-0 z-1 cursor-default"
              aria-label={labels.mapAlt}
              tabIndex={-1}
              onClick={() => setSelectedBranchId(null)}
            />

            {(() => {
              const p = geometry.project(venue);
              return (
                <button
                  type="button"
                  className="absolute z-20 inline-flex h-11 w-10 -translate-x-1/2 -translate-y-full items-start justify-center leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                  style={{ left: `${p.x}px`, top: `${p.y}px` }}
                  aria-label={`${labels.venueMarker}: ${venue.name}`}
                  title={venue.name}
                  onClick={() => setSelectedBranchId(null)}
                >
                  <CategoryIconMapPin icon={BankIcon} appearance="venue" />
                </button>
              );
            })()}

            {branchGroups
              .filter(
                ({ items }) =>
                  !selectedBranch ||
                  items.some((branch) => branch.id === selectedBranchId),
              )
              .map(({ key, items }) => {
                const isSelected = items.some(
                  (branch) => branch.id === selectedBranchId,
                );
                const p = geometry.project(items[0]);
                const venuePin = geometry.project(venue);
                const isCrowded =
                  !selectedBranch &&
                  Math.hypot(p.x - venuePin.x, p.y - venuePin.y) < 48;
                // On the all-branches overview, nearby building pins can sit
                // under the venue pin. A short leader line keeps their real
                // coordinate visible while separating the tappable icons.
                const displayX = isCrowded
                  ? Math.min(
                      Math.max(p.x + (p.x >= venuePin.x ? 52 : -52), 24),
                      geometry.width - 24,
                    )
                  : p.x;
                return (
                  <div
                    key={key}
                    className="pointer-events-none absolute inset-0 z-10"
                  >
                    {isCrowded && (
                      <svg
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full"
                      >
                        <line
                          x1={p.x}
                          y1={p.y - 4}
                          x2={displayX}
                          y2={p.y - 4}
                          stroke="#18181b"
                          strokeWidth="2"
                          strokeDasharray="3 3"
                        />
                      </svg>
                    )}
                    <button
                      type="button"
                      className="pointer-events-auto absolute z-10 inline-flex h-11 w-10 -translate-x-1/2 -translate-y-full items-start justify-center leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                      style={{ left: `${displayX}px`, top: `${p.y}px` }}
                      aria-label={items
                        .map((branch) => branch.displayName)
                        .join(", ")}
                      aria-pressed={isSelected}
                      title={items
                        .map((branch) => branch.displayName)
                        .join(", ")}
                      onClick={() => {
                        const currentIndex = items.findIndex(
                          (branch) => branch.id === selectedBranchId,
                        );
                        setSelectedBranchId(
                          items[(currentIndex + 1) % items.length].id,
                        );
                      }}
                    >
                      <CategoryIconMapPin
                        icon={StorefrontIcon}
                        selected={isSelected}
                      />
                      {items.length > 1 && (
                        <span className="pointer-events-none absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-zinc-900 px-0.5 text-[10px] font-bold text-white shadow-sm">
                          {items.length}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}

            {popupBranch &&
              selectedBranchId &&
              (() => {
                const p = geometry.project(popupBranch);
                const popup = concertBranchPopupPosition(
                  geometry,
                  p,
                  popupHeight,
                );
                return (
                  <div
                    ref={popupRef}
                    className="pointer-events-none absolute z-30 rounded-xl border border-zinc-200 bg-white/95 p-3 text-left shadow-lg backdrop-blur"
                    style={{
                      width: `${popup.width}px`,
                      left: `${popup.left}px`,
                      top: `${popup.top}px`,
                    }}
                  >
                    <p className="text-[11px] font-semibold text-orange-700">
                      {labels.branchMarker}
                    </p>
                    <p className="mt-0.5 line-clamp-2 break-words text-sm font-semibold leading-5 text-zinc-950">
                      {popupBranch.displayName}
                    </p>
                    {popupBranch.address && (
                      <p className="mt-1 line-clamp-2 break-words text-xs leading-4 text-zinc-600">
                        {popupBranch.address}
                      </p>
                    )}
                  </div>
                );
              })()}
          </>
        )}

        <div className="absolute left-3 top-3 z-20 flex flex-wrap gap-2">
          <span className="rounded-full bg-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
            {venue.name}
          </span>
          <span className="rounded-full bg-zinc-950 px-3 py-1 text-xs font-semibold text-white shadow-sm">
            {labels.branchMarker}
          </span>
        </div>

        <p className="absolute bottom-3 left-3 right-3 z-20 rounded-xl bg-white/90 px-3 py-2 text-xs leading-5 text-zinc-600 shadow-sm backdrop-blur">
          {labels.selectHint}
        </p>
      </div>

      {branches.length > 0 ? (
        <div>
          <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1 py-1">
            {branches.map((branch) => {
              const isSelected = branch.id === selectedBranchId;
              return (
                <button
                  key={branch.id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelectedBranchId(branch.id)}
                  className={cn(
                    "shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-bold transition-colors",
                    isSelected
                      ? "bg-zinc-900 text-white"
                      : "border bg-white text-zinc-900 hover:bg-zinc-50",
                  )}
                >
                  {branch.name}
                </button>
              );
            })}
          </div>
          {selectedBranch ? (
            <p className="mt-2 min-h-[1.25rem] px-1 text-xs text-zinc-500">
              {summaryLine}
            </p>
          ) : (
            <p className="mt-2 rounded-xl bg-orange-50 px-3 py-2.5 text-sm leading-6 text-orange-950">
              {labels.selectBranchDescription}
            </p>
          )}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 p-4 text-sm leading-6 text-zinc-600">
          {labels.branchesEmpty}
        </p>
      )}

      <div className="rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-lg font-semibold tracking-tight text-zinc-950">
              {selectedBranch?.name ??
                (branches.length > 0
                  ? labels.selectBranchTitle
                  : labels.allProductsTitle)}
            </p>
            {selectedBranch?.partnerName && (
              <p className="mt-0.5 text-sm font-medium text-orange-700">
                {selectedBranch.partnerName}
              </p>
            )}
            <p className="mt-1 text-xs text-zinc-500">{productTitle}</p>
            {selectedBranch && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
                {[selectedBranch.address, labels.selectedProductsDescription]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
          </div>
        </div>

        {activeProducts.length > 0 ? (
          <div
            key={selectedBranchId ?? "all"}
            className="mt-3 animate-in fade-in duration-200"
          >
            {activeProducts.map((product) => (
              <ProductCardEmbed key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <p className="mt-3 rounded-xl border border-dashed border-zinc-200 bg-zinc-50 p-4 text-sm leading-6 text-zinc-600">
            {selectedBranch
              ? labels.selectedProductsEmpty
              : labels.allProductsEmpty}
          </p>
        )}
      </div>
    </div>
  );
}
